import type {
  InvoiceListSnapshot,
  InvoiceRow,
  InvoiceListFilters,
  InvoiceListTotals,
} from "./types";
import { emptyFilters, isoDaysAgo, todayIso } from "./utils";

function makeCustomer(
  id: string,
  name: string,
  type: "individual" | "business",
  taxNumber: string | null,
  email: string | null = null,
  phone: string | null = null,
  isEfaturaRegistered = false,
): InvoiceRow["customer"] {
  return {
    id,
    name,
    email,
    phone,
    taxNumber,
    taxOffice: type === "business" ? "Kadıköy V.D." : null,
    type,
    isEfaturaRegistered,
  };
}

const SEED_INVOICE_ROWS: InvoiceRow[] = [
  // #1 paid e-Arşiv nakit
  {
    id: "inv-001",
    invoiceNumber: "EA20260414000001",
    type: "e_arsiv",
    status: "paid",
    customer: makeCustomer("c-001", "Zeynep Aksoy", "individual", "12345678901", "zeynep@mail.com", "+905551112233"),
    issuedAt: `${todayIso()}T09:45:00+03:00`,
    dueAt: null,
    subtotalMinor: 65000,
    discountMinor: 0,
    taxMinor: 13000,
    totalMinor: 78000,
    paidMinor: 78000,
    balanceMinor: 0,
    linkedOrderId: "ord-001",
    linkedBookingId: "bk-001",
    lines: [
      {
        id: "ln-1",
        sku: "GR-FULL",
        description: "Tam Bakım Paketi",
        quantity: 1,
        unitPriceMinor: 65000,
        taxRate: 0.2,
        taxMinor: 13000,
        discountMinor: 0,
        subtotalMinor: 65000,
        totalMinor: 78000,
      },
    ],
    payments: [
      { method: "cash", amountMinor: 78000, paidAt: `${todayIso()}T09:45:00+03:00`, transactionId: null },
    ],
    deliveryStatus: "sent_email",
    pdfUrl: "/mock/inv-001.pdf",
    shareUrl: "https://pay.petota.app/i/inv-001",
    gibCode: "A1B2C3D4-E5F6-7890-1234-567890ABCDEF",
    integrator: "parasut",
    integratorError: null,
    notes: null,
    createdAt: `${todayIso()}T09:42:00+03:00`,
    updatedAt: `${todayIso()}T09:46:00+03:00`,
  },
  // #2 paid card
  {
    id: "inv-002",
    invoiceNumber: "EA20260414000002",
    type: "e_arsiv",
    status: "paid",
    customer: makeCustomer("c-002", "Mehmet Demir", "individual", "23456789012", "mehmet@mail.com"),
    issuedAt: `${todayIso()}T10:20:00+03:00`,
    dueAt: null,
    subtotalMinor: 20000,
    discountMinor: 0,
    taxMinor: 4000,
    totalMinor: 24000,
    paidMinor: 24000,
    balanceMinor: 0,
    linkedOrderId: "ord-002",
    linkedBookingId: null,
    lines: [
      {
        id: "ln-2",
        sku: "GR-NAIL",
        description: "Tırnak Kesimi",
        quantity: 1,
        unitPriceMinor: 20000,
        taxRate: 0.2,
        taxMinor: 4000,
        discountMinor: 0,
        subtotalMinor: 20000,
        totalMinor: 24000,
      },
    ],
    payments: [
      { method: "card", amountMinor: 24000, paidAt: `${todayIso()}T10:21:00+03:00`, transactionId: "VP-882211" },
    ],
    deliveryStatus: "delivered",
    pdfUrl: "/mock/inv-002.pdf",
    shareUrl: "https://pay.petota.app/i/inv-002",
    gibCode: "B2C3D4E5-F6A7-8901-2345-67890ABCDEF1",
    integrator: "parasut",
    integratorError: null,
    notes: null,
    createdAt: `${todayIso()}T10:18:00+03:00`,
    updatedAt: `${todayIso()}T10:22:00+03:00`,
  },
  // #3 issued not-paid
  {
    id: "inv-003",
    invoiceNumber: "EA20260414000003",
    type: "e_arsiv",
    status: "issued",
    customer: makeCustomer("c-003", "Ayşe Çelik", "individual", "34567890123", "ayse@mail.com", "+905552223344"),
    issuedAt: `${isoDaysAgo(2)}T11:00:00+03:00`,
    dueAt: isoDaysAgo(-5),
    subtotalMinor: 45000,
    discountMinor: 0,
    taxMinor: 9000,
    totalMinor: 54000,
    paidMinor: 0,
    balanceMinor: 54000,
    linkedOrderId: "ord-003",
    linkedBookingId: "bk-003",
    lines: [
      {
        id: "ln-3a",
        sku: "VET-EXAM",
        description: "Veteriner Muayene",
        quantity: 1,
        unitPriceMinor: 45000,
        taxRate: 0.2,
        taxMinor: 9000,
        discountMinor: 0,
        subtotalMinor: 45000,
        totalMinor: 54000,
      },
    ],
    payments: [],
    deliveryStatus: "sent_email",
    pdfUrl: "/mock/inv-003.pdf",
    shareUrl: "https://pay.petota.app/i/inv-003",
    gibCode: "C3D4E5F6-A7B8-9012-3456-7890ABCDEF12",
    integrator: "parasut",
    integratorError: null,
    notes: "Müşteri 5 gün içinde havale yapacak.",
    createdAt: `${isoDaysAgo(2)}T10:55:00+03:00`,
    updatedAt: `${isoDaysAgo(2)}T11:02:00+03:00`,
  },
  // #4 e-Fatura B2B havale
  {
    id: "inv-004",
    invoiceNumber: "EF20260414000001",
    type: "e_fatura",
    status: "paid",
    customer: makeCustomer("c-004", "PetShop Holding A.Ş.", "business", "1234567890", "muhasebe@petshop.com", null, true),
    issuedAt: `${isoDaysAgo(3)}T14:30:00+03:00`,
    dueAt: isoDaysAgo(-12),
    subtotalMinor: 250000,
    discountMinor: 25000,
    taxMinor: 45000,
    totalMinor: 270000,
    paidMinor: 270000,
    balanceMinor: 0,
    linkedOrderId: "ord-004",
    linkedBookingId: null,
    lines: [
      {
        id: "ln-4",
        sku: "WHL-GROOM",
        description: "Toplu Bakım Hizmeti (10 paket)",
        quantity: 10,
        unitPriceMinor: 25000,
        taxRate: 0.2,
        taxMinor: 45000,
        discountMinor: 25000,
        subtotalMinor: 225000,
        totalMinor: 270000,
      },
    ],
    payments: [
      { method: "transfer", amountMinor: 270000, paidAt: `${isoDaysAgo(2)}T09:00:00+03:00`, transactionId: "EFT-998877" },
    ],
    deliveryStatus: "opened",
    pdfUrl: "/mock/inv-004.pdf",
    shareUrl: null,
    gibCode: "D4E5F6A7-B8C9-0123-4567-890ABCDEF123",
    integrator: "logo",
    integratorError: null,
    notes: null,
    createdAt: `${isoDaysAgo(3)}T14:28:00+03:00`,
    updatedAt: `${isoDaysAgo(2)}T09:01:00+03:00`,
  },
  // #5 draft
  {
    id: "inv-005",
    invoiceNumber: null,
    type: "e_arsiv",
    status: "draft",
    customer: makeCustomer("c-005", "Selin Y.", "individual", null, "selin@mail.com"),
    issuedAt: null,
    dueAt: null,
    subtotalMinor: 35000,
    discountMinor: 0,
    taxMinor: 7000,
    totalMinor: 42000,
    paidMinor: 0,
    balanceMinor: 42000,
    linkedOrderId: "ord-005",
    linkedBookingId: null,
    lines: [
      {
        id: "ln-5",
        sku: "GR-WASH",
        description: "Yıkama",
        quantity: 1,
        unitPriceMinor: 35000,
        taxRate: 0.2,
        taxMinor: 7000,
        discountMinor: 0,
        subtotalMinor: 35000,
        totalMinor: 42000,
      },
    ],
    payments: [],
    deliveryStatus: "not_sent",
    pdfUrl: null,
    shareUrl: null,
    gibCode: null,
    integrator: null,
    integratorError: null,
    notes: "Müşteri kart ile ödeyecek; kasada bekliyor.",
    createdAt: `${todayIso()}T11:30:00+03:00`,
    updatedAt: `${todayIso()}T11:30:00+03:00`,
  },
  // #6 failed
  {
    id: "inv-006",
    invoiceNumber: null,
    type: "e_fatura",
    status: "failed",
    customer: makeCustomer("c-006", "Vet Klinik Ltd.", "business", "9876543210", "info@vetklinik.com", null, true),
    issuedAt: `${todayIso()}T08:15:00+03:00`,
    dueAt: isoDaysAgo(-15),
    subtotalMinor: 120000,
    discountMinor: 0,
    taxMinor: 24000,
    totalMinor: 144000,
    paidMinor: 0,
    balanceMinor: 144000,
    linkedOrderId: "ord-006",
    linkedBookingId: null,
    lines: [
      {
        id: "ln-6",
        sku: "VET-PKG",
        description: "Aşı Paketi",
        quantity: 4,
        unitPriceMinor: 30000,
        taxRate: 0.2,
        taxMinor: 24000,
        discountMinor: 0,
        subtotalMinor: 120000,
        totalMinor: 144000,
      },
    ],
    payments: [],
    deliveryStatus: "not_sent",
    pdfUrl: null,
    shareUrl: null,
    gibCode: null,
    integrator: "logo",
    integratorError: "GİB-503: Servis geçici olarak kullanılamıyor.",
    notes: null,
    createdAt: `${todayIso()}T08:12:00+03:00`,
    updatedAt: `${todayIso()}T08:16:00+03:00`,
  },
  // #7 voided
  {
    id: "inv-007",
    invoiceNumber: "EA20260413000004",
    type: "e_arsiv",
    status: "voided",
    customer: makeCustomer("c-007", "Cem Yıldız", "individual", "45678901234"),
    issuedAt: `${isoDaysAgo(1)}T16:00:00+03:00`,
    dueAt: null,
    subtotalMinor: 30000,
    discountMinor: 0,
    taxMinor: 6000,
    totalMinor: 36000,
    paidMinor: 0,
    balanceMinor: 0,
    linkedOrderId: "ord-007",
    linkedBookingId: null,
    lines: [
      {
        id: "ln-7",
        sku: "GR-NAIL",
        description: "Tırnak Kesimi",
        quantity: 1,
        unitPriceMinor: 30000,
        taxRate: 0.2,
        taxMinor: 6000,
        discountMinor: 0,
        subtotalMinor: 30000,
        totalMinor: 36000,
      },
    ],
    payments: [],
    deliveryStatus: "not_sent",
    pdfUrl: "/mock/inv-007.pdf",
    shareUrl: null,
    gibCode: "E5F6A7B8-C9D0-1234-5678-90ABCDEF1234",
    integrator: "parasut",
    integratorError: null,
    notes: "Yanlış müşteri — düzeltme faturası kesildi.",
    createdAt: `${isoDaysAgo(1)}T15:55:00+03:00`,
    updatedAt: `${isoDaysAgo(1)}T16:30:00+03:00`,
  },
  // #8 corrective (paid replacement)
  {
    id: "inv-008",
    invoiceNumber: "EA20260413000005",
    type: "e_arsiv",
    status: "paid",
    customer: makeCustomer("c-008", "Ali Kaya", "individual", "56789012345", "ali@mail.com"),
    issuedAt: `${isoDaysAgo(1)}T16:32:00+03:00`,
    dueAt: null,
    subtotalMinor: 30000,
    discountMinor: 0,
    taxMinor: 6000,
    totalMinor: 36000,
    paidMinor: 36000,
    balanceMinor: 0,
    linkedOrderId: "ord-008",
    linkedBookingId: null,
    lines: [
      {
        id: "ln-8",
        sku: "GR-NAIL",
        description: "Tırnak Kesimi",
        quantity: 1,
        unitPriceMinor: 30000,
        taxRate: 0.2,
        taxMinor: 6000,
        discountMinor: 0,
        subtotalMinor: 30000,
        totalMinor: 36000,
      },
    ],
    payments: [{ method: "cash", amountMinor: 36000, paidAt: `${isoDaysAgo(1)}T16:33:00+03:00`, transactionId: null }],
    deliveryStatus: "sent_email",
    pdfUrl: "/mock/inv-008.pdf",
    shareUrl: null,
    gibCode: "F6A7B8C9-D0E1-2345-6789-0ABCDEF12345",
    integrator: "parasut",
    integratorError: null,
    notes: "inv-007 düzeltmesidir.",
    createdAt: `${isoDaysAgo(1)}T16:32:00+03:00`,
    updatedAt: `${isoDaysAgo(1)}T16:34:00+03:00`,
  },
  // #9 vadeli e-Fatura
  {
    id: "inv-009",
    invoiceNumber: "EF20260410000002",
    type: "e_fatura",
    status: "issued",
    customer: makeCustomer("c-009", "Köpek Eğitim Akademi", "business", "5555555555", "muhasebe@kea.com", null, true),
    issuedAt: `${isoDaysAgo(7)}T13:00:00+03:00`,
    dueAt: isoDaysAgo(-23),
    subtotalMinor: 380000,
    discountMinor: 0,
    taxMinor: 76000,
    totalMinor: 456000,
    paidMinor: 0,
    balanceMinor: 456000,
    linkedOrderId: "ord-009",
    linkedBookingId: null,
    lines: [
      {
        id: "ln-9",
        sku: "TR-PKG",
        description: "Eğitim Paket Hizmeti",
        quantity: 1,
        unitPriceMinor: 380000,
        taxRate: 0.2,
        taxMinor: 76000,
        discountMinor: 0,
        subtotalMinor: 380000,
        totalMinor: 456000,
      },
    ],
    payments: [],
    deliveryStatus: "delivered",
    pdfUrl: "/mock/inv-009.pdf",
    shareUrl: null,
    gibCode: "A7B8C9D0-E1F2-3456-7890-ABCDEF123456",
    integrator: "logo",
    integratorError: null,
    notes: null,
    createdAt: `${isoDaysAgo(7)}T12:58:00+03:00`,
    updatedAt: `${isoDaysAgo(7)}T13:01:00+03:00`,
  },
  // #10 partial paid
  {
    id: "inv-010",
    invoiceNumber: "EA20260412000006",
    type: "e_arsiv",
    status: "issued",
    customer: makeCustomer("c-010", "Deniz Su", "individual", "67890123456", "deniz@mail.com"),
    issuedAt: `${isoDaysAgo(4)}T17:30:00+03:00`,
    dueAt: isoDaysAgo(-10),
    subtotalMinor: 100000,
    discountMinor: 0,
    taxMinor: 20000,
    totalMinor: 120000,
    paidMinor: 60000,
    balanceMinor: 60000,
    linkedOrderId: "ord-010",
    linkedBookingId: null,
    lines: [
      {
        id: "ln-10",
        sku: "PKG-GRM",
        description: "Aylık Bakım Paketi",
        quantity: 1,
        unitPriceMinor: 100000,
        taxRate: 0.2,
        taxMinor: 20000,
        discountMinor: 0,
        subtotalMinor: 100000,
        totalMinor: 120000,
      },
    ],
    payments: [
      { method: "cash", amountMinor: 30000, paidAt: `${isoDaysAgo(4)}T17:32:00+03:00`, transactionId: null },
      { method: "card", amountMinor: 30000, paidAt: `${isoDaysAgo(2)}T10:00:00+03:00`, transactionId: "VP-110022" },
    ],
    deliveryStatus: "sent_whatsapp",
    pdfUrl: "/mock/inv-010.pdf",
    shareUrl: "https://pay.petota.app/i/inv-010",
    gibCode: "B8C9D0E1-F2A3-4567-8901-BCDEF1234567",
    integrator: "parasut",
    integratorError: null,
    notes: "Müşteri 2 taksit istedi.",
    createdAt: `${isoDaysAgo(4)}T17:28:00+03:00`,
    updatedAt: `${isoDaysAgo(2)}T10:01:00+03:00`,
  },
  // #11 overdue
  {
    id: "inv-011",
    invoiceNumber: "EA20260413000007",
    type: "e_arsiv",
    status: "issued",
    customer: makeCustomer("c-011", "Selma Uçar", "individual", "78901234567", "selma@mail.com"),
    issuedAt: `${isoDaysAgo(8)}T11:00:00+03:00`,
    dueAt: isoDaysAgo(1),
    subtotalMinor: 80000,
    discountMinor: 0,
    taxMinor: 16000,
    totalMinor: 96000,
    paidMinor: 0,
    balanceMinor: 96000,
    linkedOrderId: "ord-011",
    linkedBookingId: null,
    lines: [
      {
        id: "ln-11",
        sku: "BOARD-3D",
        description: "Konaklama (3 gün)",
        quantity: 3,
        unitPriceMinor: 27000,
        taxRate: 0.2,
        taxMinor: 16200,
        discountMinor: 0,
        subtotalMinor: 80000,
        totalMinor: 96000,
      },
    ],
    payments: [],
    deliveryStatus: "sent_email",
    pdfUrl: "/mock/inv-011.pdf",
    shareUrl: "https://pay.petota.app/i/inv-011",
    gibCode: "C9D0E1F2-A3B4-5678-9012-CDEF12345678",
    integrator: "parasut",
    integratorError: null,
    notes: null,
    createdAt: `${isoDaysAgo(8)}T10:55:00+03:00`,
    updatedAt: `${isoDaysAgo(8)}T11:01:00+03:00`,
  },
  // #12 sent
  {
    id: "inv-012",
    invoiceNumber: "EA20260414000008",
    type: "e_arsiv",
    status: "sent",
    customer: makeCustomer("c-012", "Burak T.", "individual", "89012345678", "burak@mail.com", "+905554445566"),
    issuedAt: `${todayIso()}T12:00:00+03:00`,
    dueAt: isoDaysAgo(-7),
    subtotalMinor: 50000,
    discountMinor: 0,
    taxMinor: 10000,
    totalMinor: 60000,
    paidMinor: 0,
    balanceMinor: 60000,
    linkedOrderId: "ord-012",
    linkedBookingId: null,
    lines: [
      {
        id: "ln-12",
        sku: "GR-MID",
        description: "Orta Bakım Paketi",
        quantity: 1,
        unitPriceMinor: 50000,
        taxRate: 0.2,
        taxMinor: 10000,
        discountMinor: 0,
        subtotalMinor: 50000,
        totalMinor: 60000,
      },
    ],
    payments: [],
    deliveryStatus: "sent_whatsapp",
    pdfUrl: "/mock/inv-012.pdf",
    shareUrl: "https://pay.petota.app/i/inv-012",
    gibCode: "D0E1F2A3-B4C5-6789-0123-DEF123456789",
    integrator: "parasut",
    integratorError: null,
    notes: null,
    createdAt: `${todayIso()}T11:58:00+03:00`,
    updatedAt: `${todayIso()}T12:01:00+03:00`,
  },
];

function computeTotals(rows: readonly InvoiceRow[]): InvoiceListTotals {
  const totals: InvoiceListTotals = {
    totalCount: rows.length,
    draftCount: rows.filter((r) => r.status === "draft").length,
    issuedCount: rows.filter((r) => r.status === "issued" || r.status === "sent").length,
    paidCount: rows.filter((r) => r.status === "paid").length,
    failedCount: rows.filter((r) => r.status === "failed").length,
    grossMinor: rows.reduce((s, r) => s + r.subtotalMinor, 0),
    discountMinor: rows.reduce((s, r) => s + r.discountMinor, 0),
    taxMinor: rows.reduce((s, r) => s + r.taxMinor, 0),
    netMinor: rows.reduce((s, r) => s + r.totalMinor, 0),
    paidMinor: rows.reduce((s, r) => s + r.paidMinor, 0),
    balanceMinor: rows.reduce((s, r) => s + r.balanceMinor, 0),
  };
  return totals;
}

export function getMockInvoiceSnapshot(
  filters: InvoiceListFilters = emptyFilters(),
): InvoiceListSnapshot {
  return {
    branchId: "branch-001",
    branchName: "Kadıköy Şubesi",
    filters,
    totals: computeTotals(SEED_INVOICE_ROWS),
    rows: SEED_INVOICE_ROWS,
    hasMore: false,
  };
}

export function getEmptyInvoiceSnapshot(): InvoiceListSnapshot {
  return {
    branchId: "branch-001",
    branchName: "Kadıköy Şubesi",
    filters: emptyFilters(),
    totals: computeTotals([]),
    rows: [],
    hasMore: false,
  };
}

export { SEED_INVOICE_ROWS };
